import { NetSalesRatesBarChart } from 'pages/insights-pro/marketplace-tab/components/NetSalesRatesBarChart';
import { Service, useProDataService } from 'services/data.service';
import { useModelService } from 'services/model/model.service';
import { useDataRequest } from 'hooks/useDataRequest';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { useDataServiceQueryResult } from 'hooks/useDataServiceQueryResult';
import { DataResult } from 'models/data';
import { MarketplaceInfo } from 'pages/insights-pro/marketplace-tab/model';
import { PageContainer, Tile } from 'components/Layout/PageContainer';
import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { useIntl } from 'react-intl';
import { EmptyState } from 'components/EmptyState';
import { MarketplaceNetSalesRatesTable } from 'pages/insights-pro/marketplace-tab/reports/net-sales-rates/MarketplaceNetSalesRatesTable';

export function MarketplaceNetSalesRates() {
  const intl = useIntl();
  const { fetchData } = useProDataService(Service.Marketplaces);
  const { getMeasures, getAttributes } = useModelService();

  const requestData = useDataRequest();

  const [chnMarketplace] = getAttributes(['chnMarketplace']);

  const [netSlsGmvValRate] = getMeasures(['netSlsGmvValRateExpOD']);
  const extractMarketplaceInfo = (data: DataResult) => {
    const marketplaces: MarketplaceInfo[] = data.sets.marketplaces.map((entry, index) => {
      const marketplaceName: string = chnMarketplace.getValue(entry.attributes);
      return {
        name: marketplaceName,
        isOther: false,
        color: getChartColorWithMax(index),
        // A marketplace is only active if it has at least one order
        isActive: netSlsGmvValRate.getValue(entry.values) > 0,
        entry,
      };
    });

    return marketplaces;
  };

  const dashboardState = useDataServiceQueryResult(
    requestData,
    data => fetchData('dashboard', data),
    response => {
      const isComparisonActive = Boolean(
        requestData.comparisonTimeFilter?.fromDate && requestData.comparisonTimeFilter?.toDate
      );

      const marketplaces = extractMarketplaceInfo(response);

      return {
        data: response,
        isComparisonActive,
        countryCountCompare: isComparisonActive
          ? response.sets.marketplaces.filter(country => netSlsGmvValRate.getValue(country.compareValues) > 0).length
          : null,
        marketplaces,
      };
    },
    ['insights-marketplaces-net-sales-rates-dashboard']
  );

  if (!dashboardState.data && !dashboardState.isError) {
    return <MetricsLoadingSpinner />;
  }

  const dashboardData = dashboardState.data;
  const isComparisonActive = dashboardData?.isComparisonActive ?? false;

  return (
    <PageContainer>
      <Tile className="text-lg p-4" titleId="insights.marketplace.reports.net_sales_rates" />
      <Tile
        isError={dashboardState.isError}
        titleId="model.metric.netSlsGmvValRate.name"
        additionalTitle={intl.formatMessage({
          id: 'insights.marketplace_tab.marketplace_return_rates_header_subheader',
          defaultMessage: 'per Marketplace',
        })}
        errorDetails={dashboardState.error}
      >
        {dashboardData ? (
          dashboardData.marketplaces.length === 0 ? (
            <div className="col-span-6 xl:col-span-12 flex justify-center mt-4">
              <EmptyState />
            </div>
          ) : (
            <NetSalesRatesBarChart
              testId="net-sales-bar-chart"
              total={dashboardData.data}
              marketplaces={dashboardData.marketplaces}
              isComparisonActive={isComparisonActive}
            />
          )
        ) : null}
      </Tile>
      <MarketplaceNetSalesRatesTable />
    </PageContainer>
  );
}
